
/* System defined core library */
import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { MetricApiService } from '../../shared/services/metric-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';

/* User defined enums/constants */
import { QrPaymentCodes, TransactionType } from '../../../shared/constants/scheduling.constant';
import { ComponentType, Fee, LogLevel } from '../../../shared/enums/scheduling.enum';
import { BatteryConfig } from '../../../shared/models/scheduling/batteryConfig.model';
import { PaymentFeeDetail } from '../../../shared/models/scheduling/recheduleFee.model';

@Component({
  selector: 'app-cancellation',
  templateUrl: './cancellation.component.html'
})
export class CancellationComponent implements OnInit {

  /* User defined private/public variables  */
  public targetFlow: string = "Cancellation";
  public isNextButtonEnabled: boolean = false;;
  public examCount: number = -1;
  public isValid: boolean = true;
  public isValidTo: boolean = true;
  public booking: Booking;
  public isCancelAllowed: boolean = true;
  public pathLock = false;
  public refundAmount: number = 0;
  public invalidScheduleMethod: boolean = false;
  public batteryConfig: BatteryConfig;
  public selectedAppointments: AppointmentDetail[] = [];
  public cancelNotAllowed = false;

  /* Service subscriptions */
  booking$: Subscription;
  translateLanguage$: Subscription;
  batteryConfig$: Subscription;
  selectedAppointments$: Subscription;
  cancelNotAllowed$: Subscription;

  constructor(
    private metricApiService: MetricApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {
    this.booking$ = selectedExamDetailService.booking.subscribe(
      booking => {
        this.booking = booking;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.batteryConfig$ = selectedExamDetailService.batteryConfig.subscribe(
      batteryConfig => {
        this.batteryConfig = batteryConfig;
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.cancelNotAllowed$ = selectedExamDetailService.cancelNotAllowed.subscribe(
      cancelNotAllowed => {
        this.cancelNotAllowed = cancelNotAllowed;
      });
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.CancellationComponent)) return;
    this.pathLock = this.selectedExamDetailService.setCancelPathLock;
  }

  /* use to navigate on next page. */
  submit(event: any): void {
    this.selectedExamDetailService.appointmentCountForInfo.next(this.examCount - 1);
    this.routingService.cancelFwdNavigation(ComponentType.CancellationComponent);
  }

  /* use to move back from current page. */
  goBack(): void {
    this.routingService.backwardNavigation(ComponentType.CancellationComponent);
  }

  /* Enable next button if any appointment is selected */
  enableNextButton(isAppointmentSelected: boolean): void {
    this.isNextButtonEnabled = isAppointmentSelected;
  }

  /* use to move back from current page. */
  setSelectedAppointments(appointmentData: AppointmentDetail[]): void {
    this.refundAmount = 0;
    this.isCancelAllowed = true;
    this.examCount = appointmentData.length;
    this.selectedExamDetailService.cancelRefundQrTransaction = undefined;
    this.selectedExamDetailService.setSelectedAppointmentDetails(appointmentData);
    if (!!this.selectedAppointments.length) {
      let examId = this.selectedAppointments[0].exam.id;
      this.schedulingApiService.getBookingPaymentRefundDetail(this.booking.id, examId).subscribe(
        (response: any) => {
          if (response && response.status === 404) this.isCancelAllowed = false;
          else {
            if (response && response.length) {
              this.selectedAppointments.forEach((appt) => {
                const refundablePayment = appt.payments.find((payment) => {
                  return (payment.transactionType === TransactionType.Schedule);
                });

                let results = response.find((refund: PaymentFeeDetail) => {
                  return refund.id === appt.exam.id;
                });

                let tmpPayment: Payment = new Payment(
                  null,
                  results.currency || "USD",
                  Fee.Refund,
                  null, null, null,
                  null, null, null,
                  null, null,
                  (!!refundablePayment ? refundablePayment.paymentType : null),
                  results.amount
                );
                appt.payments.push(tmpPayment);
                this.refundAmount += tmpPayment.refund;
                this.selectedExamDetailService.cancelRefund = this.refundAmount;
                this.selectedExamDetailService.cancelRefundCurrency = results.currency;
                this.selectedExamDetailService.cancelRefundPaymentType = (!!refundablePayment ? refundablePayment.paymentType : this.selectedExamDetailService.cancelRefundPaymentType);
                if (!!refundablePayment && !!refundablePayment.paymentType && QrPaymentCodes.indexOf(refundablePayment.paymentType.toLowerCase()) !== -1) {
                  this.selectedExamDetailService.cancelRefundQrTransaction = refundablePayment;
                }
              });
            }
          }
        },
        err => this.isCancelAllowed = false
      );
    }
  }

  isDisable(): boolean {
    return !this.isNextButtonEnabled || this.selectedExamDetailService.programNotPublished;
  }

  isAllExamAllowedToCancelEmit($event: any) {
    this.isValid = $event;
  }

  isAllExamValidEmit($event: any) {
    this.isValidTo = $event;
  }

  isScheduleMethodValid($event: any) {
    this.invalidScheduleMethod = $event;
    this.isValid = !$event;
  }

  ssoClientEmit($event: boolean) {
    if ($event) {
      this.metricApiService.log(LogLevel.Debug, 'User attempted to cancel SSO appointment from home page: User Redirected to error page.');
      this.routingService.invalidNavigation();
    }
  }

  getPathLock() {
    return this.selectedExamDetailService.setCancelPathLock;
  }

}
